/**
 * statementMath — every number on the invoice balance statement.
 *
 * Pure functions only, so the figures can be tested without a browser. Money
 * bugs are the expensive kind here: this feeds a document showing a client what
 * they still owe.
 *
 * Conventions (all decided with the user, not assumed):
 *   • Everything is computed EX-GST. GST and inc-GST are presented separately.
 *   • ALL linked invoices count toward invoiced-to-date, whatever their status;
 *     each line carries its own status badge.
 *   • Adjustments are SIGNED and move the BALANCE only — never the contract.
 *   • A project with no linked quote has NO contract value. That is reported as
 *     null, not 0, so "nothing quoted" cannot be misread as "nothing owing".
 */
import { computeQuoteFinancials } from './pdfExport';
import { computeInvoiceFinancials } from './invoicePdfExport';

export const GST_RATE = 0.1;

/** Round to cents. Float sums drift: 0.1 + 0.2 is 0.30000000000000004. */
export function money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Contract value = sum of the linked quotes, ex-GST.
 *
 * MUST go through computeQuoteFinancials: when a quote has amount_enabled
 * false, the typed manual_subtotal is the real figure and the stored
 * `subtotal` field is the (higher) category sum. Reading `quote.subtotal`
 * directly overstates two of the five linked quotes in production.
 *
 * Returns null when there are no quotes at all.
 */
export function contractValue(quotes) {
  if (!quotes || quotes.length === 0) return null;
  return money(
    quotes.reduce((sum, q) => sum + (computeQuoteFinancials(q).subtotal || 0), 0)
  );
}

/** One statement line per invoice, ex-GST, with the description auto-filled. */
export function invoiceLines(invoices) {
  return [...(invoices || [])]
    .sort((a, b) => String(a.invoice_no || '').localeCompare(String(b.invoice_no || '')))
    .map(inv => ({
      id: inv.id,
      invoice_no: inv.invoice_no || '—',
      date: inv.invoice_date || '',
      status: inv.status || 'draft',
      description: invoiceDescription(inv),
      amount: money(computeInvoiceFinancials(inv).subtotal),
    }));
}

/**
 * Auto-filled description for an invoice line.
 *
 * Prefers the invoice's own line items, since that is what the client was
 * actually billed for. Falls back to the project name, then the invoice number
 * — never invents text.
 */
export function invoiceDescription(inv) {
  const items = (inv?.items || []).filter(i => (i.description || '').trim());
  if (items.length === 1) return items[0].description.trim();
  if (items.length > 1) {
    return `${items[0].description.trim()} +${items.length - 1} more`;
  }
  if (inv?.project_name) return inv.project_name;
  return inv?.invoice_no ? `Invoice ${inv.invoice_no}` : 'Invoice';
}

/** Signed sum of the manual adjustment lines, ex-GST. */
export function adjustmentTotal(adjustments) {
  return money(
    (adjustments || []).reduce((sum, a) => sum + (Number(a.amount) || 0), 0)
  );
}

/**
 * The whole statement.
 *
 *   balance = contract − invoiced + adjustments        (all ex-GST)
 *
 * Sign check, because getting this backwards overcharges a client:
 * a $5,000 discount is amount = −5000, and it must LOWER the balance.
 *   564,500 − 262,490 + (−5,000) = 297,010   ✓ client owes 5,000 less
 * Subtracting the adjustment instead would give 307,010 — a discount that
 * increases the bill.
 */
export function computeStatement({ quotes, invoices, adjustments }) {
  const contract = contractValue(quotes);
  const lines = invoiceLines(invoices);
  const invoiced = money(lines.reduce((s, l) => s + l.amount, 0));
  const adjust = adjustmentTotal(adjustments);

  const balance = contract === null ? null : money(contract - invoiced + adjust);

  return {
    hasContract: contract !== null,
    contract,
    contractGst: contract === null ? null : money(contract * GST_RATE),
    contractIncGst: contract === null ? null : money(contract * (1 + GST_RATE)),

    lines,
    invoiced,
    invoicedGst: money(invoiced * GST_RATE),
    invoicedIncGst: money(invoiced * (1 + GST_RATE)),

    adjustments: adjust,

    balance,
    balanceGst: balance === null ? null : money(balance * GST_RATE),
    balanceIncGst: balance === null ? null : money(balance * (1 + GST_RATE)),

    // Useful signals for the UI rather than silent oddities.
    overInvoiced: balance !== null && balance < 0,
    draftCount: lines.filter(l => l.status === 'draft').length,
    draftTotal: money(lines.filter(l => l.status === 'draft').reduce((s, l) => s + l.amount, 0)),
  };
}
